import { createAuthenticationClient } from '@adornis/authentication/client/authentication-client.js';
import type { AuthenticationMethod } from '@adornis/authentication/core/authentication-methods.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import '@adornis/user-forms/x-login.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html, nothing } from 'lit';
import * as m from '../../localization/out/messages.js';
import { getCompanyByUserID } from '../api/company.js';
import { subscribeMatchingWeekSettings } from '../api/settings.js';
import { MatchingWeekRole } from '../db/enums/roles.js';
import { DefaultFullPageLoader } from './components/loaders.js';
import { UserToolbar } from './components/user-toolbar.js';
import { adminGuard, roleHide } from './guards/admin-guard.js';
import { pageTitle } from './page-title.js';
import { MwActionLogin } from './pages/actions/mw-action-login.js';
import { adminRouter } from './pages/admin/admin-routes.js';
import { MwAdminLayout } from './pages/admin/mw-admin-layout.js';
import { BookAppointmentPage } from './pages/booking/book-appointment.js';
import { CandidateProfile } from './pages/candidate/candidate-profile.js';
import { companyRouter } from './pages/company/company-routes.js';
import { MwCompanyDashboard } from './pages/company/mw-company-dashboard.js';
import { PublicCompanyTerms } from './pages/public/public-company-terms.js';
import { PublicImprint } from './pages/public/public-imprint.js';
import { PublicPrivacy } from './pages/public/public-privacy.js';
import { CompanyRegistrationForm } from './pages/register/company-registration-form.js';
import { RegisterLoginOrRegister } from './pages/register/register-login-or-register.js';
import { registerRouter } from './pages/register/register-routes.js';
import '@adornis/buchungsheld-components/authentication/bh-login.js';
import '@adornis/buchungsheld-theme/index.js';
import logo from './assets/logo.jpeg';

const authenticationClient = createAuthenticationClient<readonly AuthenticationMethod[]>([], {
  authenticationOrigin: window.location.origin,
});

const publicFooter = () => html`
  <footer class="bg-base-100 border-t border-base-300 mt-auto">
    <div class="max-w-6xl mx-auto px-4 py-4 flex items-center justify-center gap-6 text-sm">
      <a href="/imprint" class="link link-hover">${m.imprint()}</a>
      <a href="/privacy" class="link link-hover">${m.privacy_policy()}</a>
    </div>
  </footer>
`;

const publicHeader = () => html`
  <header class="bg-base-100 shadow-sm">
    <div class="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
      <a href="/" class="flex items-center gap-3">
        <img src=${logo} alt="ICS RM" class="h-10 w-auto" />
        <span class="text-xl sm:text-2xl font-bold">${m.welcome_back_title()}</span>
      </a>
      ${UserToolbar()}
    </div>
  </header>
`;

const loginScreen = () => html`
  <div class="min-h-dvh flex items-center justify-center p-4 bg-base-200">
    <div class="w-full max-w-md card bg-base-100 shadow-xl">
      <div class="card-body">
        <h2 class="card-title">${m.login_card_title()}</h2>
        <p class="text-sm text-base-content/70 mb-2">${m.login_card_desc()}</p>
        <bh-login></bh-login>
      </div>
    </div>
  </div>
`;

export const mainRouter = makeRouter({
  root: {
    path: '/',
    documentTitlePart: pageTitle(m.dashboard_title),
    render: stateful(() => {
      const user = useQuery(() => currentUser<User>(), []);
      const settingsQuery = useQuery(() => subscribeMatchingWeekSettings()({ companyRegistrationClosed: 1 }), []);
      const companyRegistrationClosed = settingsQuery.value?.companyRegistrationClosed === true;
      const company = useQuery(async () => {
        if (!user.value?._id) return null;
        return await getCompanyByUserID(user.value._id)({ _id: 1, name: 1 });
      }, [user.value?._id]);

      // candidates should be redirected
      useEffect(() => {
        if (
          !user.value?.roles?.map(role => role.name).includes(MatchingWeekRole.ADMIN) &&
          !user.value?.roles?.map(role => role.name).includes(MatchingWeekRole.COMPANY) &&
          user.value
        ) {
          mainRouter.goTo('candidate', { replaceState: true });
        }
      }, [user.value?.roles]);

      return user.value
        ? html`
            <div class="min-h-dvh p-6">
              <div class="max-w-5xl mx-auto">
                <div class="mb-6 flex items-center justify-between">
                  <h1 class="text-3xl font-bold">${m.dashboard_title()}</h1>
                  <!-- <button class="btn btn-outline" @click=${logout}>${m.logout()}</button> -->
                  <!-- TODO: replace with usertoolbar -->
                  ${UserToolbar()}
                </div>
                <div class="grid gap-6 sm:grid-cols-2">
                  ${roleHide(
                    user.value,
                    [MatchingWeekRole.COMPANY, MatchingWeekRole.ADMIN],
                    () =>
                      html`<a href="/company" class="card bg-base-200 hover:shadow-lg transition-shadow">
                        <div class="card-body">
                          <h2 class="card-title">${company.value?.name}</h2>
                          <p>
                            ${m.welcome_user_message({
                              firstName: user.value?.firstName ?? '',
                              lastName: user.value?.lastName ?? '',
                            })}
                          </p>
                          <div class="card-actions justify-end">
                            <button class="btn btn-primary">${m.open()}</button>
                          </div>
                        </div>
                      </a>`,
                  )}
                  ${roleHide(
                    user.value,
                    [MatchingWeekRole.ADMIN],
                    () =>
                      html`<a href="/admin" class="card bg-base-200 hover:shadow-lg transition-shadow">
                        <div class="card-body">
                          <h2 class="card-title">${m.admin_dashboard_card_title()}</h2>
                          <p>${m.admin_dashboard_card_desc()}</p>
                          <div class="card-actions justify-end">
                            <button class="btn btn-secondary">${m.open()}</button>
                          </div>
                        </div>
                      </a>`,
                  )}
                </div>
              </div>
            </div>
          `
        : html`
            <div class="min-h-dvh flex flex-col bg-base-200">
              ${publicHeader()}
              <main class="flex-1 overflow-y-auto">
                <div class="max-w-3xl mx-auto px-4 py-8 sm:py-10 space-y-6">
                  <p class="text-base-content/70">${m.login_page_intro()}</p>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div class="bg-base-100 rounded-lg p-4 border border-base-300">
                      <div class="font-semibold">${m.stats_for_companies_title()}</div>
                      <div class="text-sm text-base-content/70 mt-1">${m.stats_for_companies_desc()}</div>
                    </div>
                    <div class="bg-base-100 rounded-lg p-4 border border-base-300">
                      <div class="font-semibold">${m.stats_for_candidates_title()}</div>
                      <div class="text-sm text-base-content/70 mt-1">${m.stats_for_candidates_desc()}</div>
                    </div>
                  </div>

                  ${
                    companyRegistrationClosed
                      ? html`
                          <div class="alert alert-warning shadow-lg">
                            <span class="icon-[material-symbols--warning] w-6 h-6"></span>
                            <span>${m.company_registration_closed_notice()}</span>
                          </div>
                        `
                      : nothing
                  }
                  ${
                    companyRegistrationClosed
                      ? html`<button class="btn btn-primary btn-disabled w-full" disabled>
                          ${m.register_as_company_button()}
                        </button>`
                      : html`<a href="/register-company" class="btn btn-primary w-full">
                          ${m.register_as_company_button()}
                        </a>`
                  }

                  <div class="card bg-base-100 shadow-xl">
                    <div class="card-body">
                      <h2 class="card-title">${m.login_card_title()}</h2>
                      <p class="text-sm text-base-content/70 mb-2">${m.login_card_desc()}</p>
                      <bh-login></bh-login>
                    </div>
                  </div>
                </div>
              </main>
              ${publicFooter()}
            </div>
          `;
    }),
  },
  embed: { path: '/embed{/*}?*', documentTitlePart: '', subRouter: registerRouter },
  company: {
    path: '/company{/*}?*',
    documentTitlePart: '',
    render: () =>
      authenticationGuard(authenticationClient, () => MwCompanyDashboard(), loginScreen, DefaultFullPageLoader),
    subRouter: companyRouter,
  },
  admin: {
    path: '/admin{/*}?*',
    documentTitlePart: '',
    render: stateful(() => {
      const user = useQuery(() => currentUser<User>(), []);
      if (user.loading) return DefaultFullPageLoader();
      return authenticationGuard(
        authenticationClient,
        () => adminGuard(user.value, () => MwAdminLayout()),
        loginScreen,
        DefaultFullPageLoader,
      );
    }),
    subRouter: adminRouter,
  },
  candidate: {
    path: '/candidate{/*}?*',
    documentTitlePart: pageTitle(m.candidate_profile_title_application_overview),
    render: () =>
      authenticationGuard(authenticationClient, () => CandidateProfile(), loginScreen, DefaultFullPageLoader),
  },
  bookAppointment: {
    path: '/book-appointment{/:jobID}?',
    documentTitlePart: pageTitle(m.candidate_book_appointment_button),
    render: ({ jobID }) =>
      authenticationGuard(
        authenticationClient,
        () => BookAppointmentPage({ jobID }),
        () => html`
          <div class="min-h-screen flex items-center justify-center p-4 bg-base-200">
            <div class="w-full max-w-md">${RegisterLoginOrRegister()}</div>
          </div>
        `,
      ),
  },
  registration: {
    path: '/register-company',
    documentTitlePart: pageTitle(m.company_registration_page_title),
    render: () => CompanyRegistrationForm(),
  },
  actionLogin: {
    // Magic-login landing for emailed tokens. The page exchanges `?token=…`
    // for a session via `User.loginWithToken` and bounces back to `/`.
    path: '/action/login',
    documentTitlePart: pageTitle(m.login_card_title),
    render: () => MwActionLogin(),
  },
  imprint: { path: '/imprint', documentTitlePart: pageTitle(m.imprint), render: () => PublicImprint() },
  privacy: { path: '/privacy', documentTitlePart: pageTitle(m.privacy_policy), render: () => PublicPrivacy() },
  companyTerms: {
    // NB: paths starting with `/company` are swallowed by the company router
    // above (the trailing `*` in '/company{/*}?*' is greedy), so we serve the
    // public terms page at `/legal/company-terms` instead.
    path: '/legal/company-terms',
    documentTitlePart: pageTitle(m.page_title_company_terms),
    render: () => PublicCompanyTerms(),
  },
}, { routerDocumentTitle: 'Matching Week' }).init();
